import { useEffect, useState, useCallback } from "react";
import { Instagram, X, ArrowLeft, ArrowRight } from "lucide-react";
import { fetchGallery } from "@/lib/api";

const ASPECTS = ["aspect-[4/5]", "aspect-video", "aspect-square", "aspect-[3/4]", "aspect-video", "aspect-[4/5]"];

export const PhotoGallery = () => {
  const [gallery, setGallery] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    fetchGallery().then(setGallery).catch(() => {});
  }, []);

  const frames = gallery?.frames || [];

  const step = useCallback(
    (dir) => {
      setLightbox((i) => (i === null ? null : (i + dir + frames.length) % frames.length));
    },
    [frames.length]
  );

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightbox, step]);

  if (!frames.length) return null;

  return (
    <section data-testid="photo-gallery-section" className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32 border-t border-zinc-900">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-14">
        <div>
          <p className="text-[11px] uppercase tracking-[0.3em] text-zinc-500 mb-6">
            Selected frames · From the field & the set
          </p>
          <h2 className="font-serif text-4xl md:text-6xl leading-tight max-w-2xl">
            Moments held <span className="italic text-zinc-400">still.</span>
          </h2>
        </div>
        {gallery?.instagram && (
          <a
            href={gallery.instagram.url}
            target="_blank"
            rel="noreferrer"
            data-testid="instagram-follow-btn"
            className="group inline-flex items-center gap-3 bg-white text-zinc-950 px-7 py-4 text-[11px] uppercase tracking-[0.3em] hover:bg-zinc-200 transition-colors self-start"
          >
            <Instagram className="h-4 w-4" strokeWidth={1.5} />
            Follow {gallery.instagram.handle}
          </a>
        )}
      </div>

      <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:balance]">
        {frames.map((f, i) => {
          const zoom = f.src.includes("sddefault")
            ? "scale-[1.35] group-hover:scale-[1.42]"
            : "group-hover:scale-105";
          return (
          <button
            key={f.id}
            onClick={() => setLightbox(i)}
            data-testid={`gallery-frame-${f.id}`}
            className={`group relative block w-full mb-5 overflow-hidden bg-zinc-900 break-inside-avoid ${ASPECTS[i % ASPECTS.length]}`}
          >
            <img
              src={f.src}
              alt={f.caption}
              loading="lazy"
              className={`absolute inset-0 w-full h-full object-cover grayscale-[35%] group-hover:grayscale-0 transition-all duration-700 ${zoom}`}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <div className="absolute bottom-0 left-0 right-0 p-5 text-left opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-500">
              <p className="font-serif text-lg text-zinc-100 leading-tight">{f.caption}</p>
              <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 mt-1.5">
                {f.category} · {f.year}
              </p>
            </div>
          </button>
          );
        })}
      </div>

      <p className="mt-12 text-zinc-500 text-sm leading-relaxed max-w-xl">
        These frames are pulled from films I've directed and produced. The full stills
        archive — and the newest work — lives on{" "}
        <a
          href={gallery?.instagram?.url}
          target="_blank"
          rel="noreferrer"
          data-testid="instagram-inline-link"
          className="text-zinc-300 hover:text-white underline underline-offset-4 decoration-zinc-700 hover:decoration-white transition-colors"
        >
          Instagram
        </a>
        .
      </p>

      {lightbox !== null && frames[lightbox] && (
        <div
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-[100] bg-zinc-950/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-12"
          onClick={() => setLightbox(null)}
        >
          <button
            data-testid="lightbox-close-btn"
            onClick={() => setLightbox(null)}
            className="absolute top-6 right-6 text-zinc-400 hover:text-white transition-colors z-10"
            aria-label="Close"
          >
            <X className="h-7 w-7" strokeWidth={1.5} />
          </button>
          <button
            data-testid="lightbox-prev-btn"
            onClick={(e) => { e.stopPropagation(); step(-1); }}
            className="absolute left-4 md:left-8 text-zinc-400 hover:text-white transition-colors z-10"
            aria-label="Previous"
          >
            <ArrowLeft className="h-7 w-7" strokeWidth={1.5} />
          </button>
          <button
            data-testid="lightbox-next-btn"
            onClick={(e) => { e.stopPropagation(); step(1); }}
            className="absolute right-4 md:right-8 text-zinc-400 hover:text-white transition-colors z-10"
            aria-label="Next"
          >
            <ArrowRight className="h-7 w-7" strokeWidth={1.5} />
          </button>
          <figure className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={frames[lightbox].src}
              alt={frames[lightbox].caption}
              className="w-full max-h-[75vh] object-contain"
            />
            <figcaption className="mt-6 flex items-baseline justify-between gap-6">
              <span className="font-serif text-xl md:text-2xl text-zinc-100">
                {frames[lightbox].caption}
              </span>
              <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 tabular-nums shrink-0">
                {String(lightbox + 1).padStart(2, "0")} / {String(frames.length).padStart(2, "0")}
              </span>
            </figcaption>
          </figure>
        </div>
      )}
    </section>
  );
};
